import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { localSafetyFormService, LocalSafetyForm } from '../services/localDataService';
import { safetyFormService } from '../services/amplifyDataService';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { toast } from 'react-toastify';
import './Screen.css';

export default function PTPReview() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isOnline = useNetworkStatus();

  const [ptp, setPtp] = useState<LocalSafetyForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [crewMemberName, setCrewMemberName] = useState('');
  const [crewSignature, setCrewSignature] = useState<SignatureCanvas | null>(null);
  const [acknowledgedHazards, setAcknowledgedHazards] = useState<Set<number>>(new Set());
  const [acknowledgedTasks, setAcknowledgedTasks] = useState<Set<number>>(new Set());
  const [nobodyHurt, setNobodyHurt] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (id) {
      loadPTP(id);
    }
  }, [id]);

  const loadPTP = async (ptpId: string) => {
    try {
      // Try Amplify DataStore first (Gen 1)
      try {
        const form = await safetyFormService.getById(ptpId);
        if (form) {
          setPtp(form as any);
          setLoading(false);
          return;
        }
      } catch (amplifyError) {
        // Fallback to local service
        console.log('Amplify DataStore not available, using local service');
      }
      
      const form = await localSafetyFormService.getById(ptpId);
      if (form) {
        setPtp(form);
      }
    } catch (error) {
      console.error('Failed to load PTP:', error);
      toast.error('Failed to load PTP');
    } finally {
      setLoading(false);
    }
  };

  const toggleHazardAcknowledgment = (index: number) => {
    setAcknowledgedHazards((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const toggleTaskAcknowledgment = (index: number) => {
    setAcknowledgedTasks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleSignOff = async () => {
    if (!crewMemberName.trim() || !crewSignature) {
      alert('Please enter your name and provide a signature');
      return;
    }

    setSaving(true);
    try {
      const signatureData = crewSignature.toDataURL();
      let crewSignatures: Array<{ name: string; signature: string; timestamp: string }> = [];
      
      // Handle both string (JSON) and array formats
      if (ptp?.crewSignatures) {
        if (typeof ptp.crewSignatures === 'string') {
          try {
            crewSignatures = JSON.parse(ptp.crewSignatures);
          } catch {
            crewSignatures = [];
          }
        } else if (Array.isArray(ptp.crewSignatures)) {
          crewSignatures = ptp.crewSignatures;
        }
      }
      
      const newSignature = {
        name: crewMemberName,
        signature: signatureData,
        timestamp: new Date().toISOString(),
      };

      const updates = {
        crewSignatures: JSON.stringify([...crewSignatures, newSignature]),
        status: 'in_progress' as const,
      };

      // Use Amplify DataStore (Gen 1) - handles offline sync automatically
      try {
        await safetyFormService.update(id!, updates);
        toast.success('Signature submitted successfully');
        // DataStore automatically handles offline sync - no custom code needed!
      } catch (amplifyError: any) {
        // Fallback to local storage if Amplify not available
        console.warn('Amplify DataStore not available, using local storage:', amplifyError);
        await localSafetyFormService.update(id!, {
          ...updates,
          updatedAt: new Date().toISOString(),
        });
        toast.info('Signature saved locally (AWS not configured)');
      }

      navigate('/ptp');
    } catch (error: any) {
      console.error('Failed to submit signature:', error);
      toast.error(`Failed to submit signature: ${error?.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleEndOfDaySignOut = async () => {
    if (!nobodyHurt) {
      alert('Please acknowledge that nobody was hurt');
      return;
    }

    setSaving(true);
    try {
      const updates = {
        nobodyHurtAcknowledgment: true,
        nobodyHurtDate: new Date().toISOString(),
        status: 'completed' as const,
      };

      // Use Amplify DataStore (Gen 1) - handles offline sync automatically
      try {
        await safetyFormService.update(id!, updates);
        toast.success('End-of-day sign-out completed successfully');
        // DataStore automatically handles offline sync - no custom code needed!
      } catch (amplifyError: any) {
        // Fallback to local storage if Amplify not available
        console.warn('Amplify DataStore not available, using local storage:', amplifyError);
        await localSafetyFormService.update(id!, {
          ...updates,
          updatedAt: new Date().toISOString(),
        });
        toast.info('End-of-day sign-out saved locally (AWS not configured)');
      }

      navigate('/ptp');
    } catch (error: any) {
      console.error('Failed to complete sign-out:', error);
      toast.error(`Failed to complete sign-out: ${error?.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading PTP...</div>;
  }

  if (!ptp) {
    return (
      <div className="screen">
        <div className="error-message">PTP not found</div>
        <button onClick={() => navigate('/ptp')} className="btn btn-primary">
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="screen">
      <div className="screen-header">
        <h1>Review Pre-Task Planning (PTP)</h1>
        <p className="screen-subtitle">Review hazards, tasks, and sign off before starting work</p>
      </div>

      {/* PTP Information */}
      <div className="ptp-info-card">
        <h3>PTP Information</h3>
        <div className="info-grid">
          <div><strong>Project:</strong> {ptp.projectName || 'N/A'}</div>
          <div><strong>Location:</strong> {ptp.location || 'N/A'}</div>
          <div><strong>Work Date:</strong> {ptp.workDate ? new Date(ptp.workDate).toLocaleDateString() : 'N/A'}</div>
          <div><strong>Foreman:</strong> {ptp.foremanName || 'N/A'}</div>
        </div>
      </div>

      {/* Required Permits */}
      {ptp.requiredPermits && ptp.requiredPermits.length > 0 && (
        <div className="form-section">
          <h2>Required Permits</h2>
          <ul className="checklist">
            {ptp.requiredPermits.map((permit, index) => (
              <li key={index}>✓ {permit}</li>
            ))}
          </ul>
        </div>
      )}

      {/* PPE */}
      {ptp.ppe && (
        <div className="form-section">
          <h2>Personal Protective Equipment (PPE)</h2>
          <div className="info-box">{ptp.ppe}</div>
        </div>
      )}

      {/* Hazards Review */}
      {ptp.hazards && ptp.hazards.length > 0 && (
        <div className="form-section">
          <h2>Identified Hazards - Review and Acknowledge</h2>
          <p className="section-description">
            Please review each hazard and acknowledge that you understand it.
          </p>
          <div className="hazards-list">
            {ptp.hazards.map((hazard, index) => (
              <div key={index} className="hazard-item">
                <label className="hazard-checkbox">
                  <input
                    type="checkbox"
                    checked={acknowledgedHazards.has(index)}
                    onChange={() => toggleHazardAcknowledgment(index)}
                  />
                  <span className="hazard-text">{hazard}</span>
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Control Measures */}
      {ptp.controlMeasures && ptp.controlMeasures.length > 0 && (
        <div className="form-section">
          <h2>Control Measures</h2>
          <ul className="checklist">
            {ptp.controlMeasures.map((measure, index) => (
              <li key={index}>• {measure}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Tasks Review */}
      {ptp.tasks && ptp.tasks.length > 0 && (
        <div className="form-section">
          <h2>Tasks - Review and Acknowledge</h2>
          <p className="section-description">
            Please review each task and acknowledge that you understand your responsibilities.
          </p>
          <div className="tasks-list">
            {ptp.tasks.map((task, index) => (
              <div key={index} className="task-item">
                <label className="task-checkbox">
                  <input
                    type="checkbox"
                    checked={acknowledgedTasks.has(index)}
                    onChange={() => toggleTaskAcknowledgment(index)}
                  />
                  <span className="task-text">{task}</span>
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Emergency Information */}
      <div className="form-section">
        <h2>Emergency Information</h2>
        <div className="info-box">
          <p><strong>Emergency Action Plan:</strong> {ptp.emergencyActionPlan ? 'Yes' : 'No'}</p>
          <p><strong>Muster Area:</strong> {ptp.emergencyMusterArea || 'N/A'}</p>
          <p><strong>Emergency Contacts:</strong></p>
          <pre>{ptp.emergencyContacts || 'N/A'}</pre>
        </div>
      </div>

      {/* Crew Member Sign-Off */}
      <div className="form-section">
        <h2>Crew Member Sign-Off</h2>
        <div className="form-group">
          <label>Your Name *</label>
          <input
            type="text"
            value={crewMemberName}
            onChange={(e) => setCrewMemberName(e.target.value)}
            placeholder="Enter your full name"
            required
          />
        </div>
        <div className="form-group">
          <label>Digital Signature *</label>
          <div className="signature-container">
            <SignatureCanvas
              ref={(ref: any) => setCrewSignature(ref)}
              canvasProps={{
                width: 500,
                height: 200,
                className: 'signature-canvas',
              }}
            />
            <div className="signature-actions">
              <button
                type="button"
                onClick={() => crewSignature?.clear()}
                className="btn btn-secondary"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
        <button
          onClick={handleSignOff}
          className="btn btn-primary"
          disabled={saving || !crewMemberName.trim() || !crewSignature}
        >
          {saving ? 'Submitting...' : 'Sign Off'}
        </button>
      </div>

      {/* End of Day Sign-Out (UC-3) */}
      {ptp.status === 'in_progress' && (
        <div className="form-section">
          <h2>End of Day Sign-Out</h2>
          <p className="section-description">
            At the end of the shift, acknowledge that work was completed safely.
          </p>
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={nobodyHurt}
                onChange={(e) => setNobodyHurt(e.target.checked)}
              />
              <span>I acknowledge that <strong>Nobody Hurt</strong> during this work shift.</span>
            </label>
          </div>
          <button
            onClick={handleEndOfDaySignOut}
            className="btn btn-success"
            disabled={saving || !nobodyHurt}
          >
            {saving ? 'Completing...' : 'Complete End of Day Sign-Out'}
          </button>
        </div>
      )}

      {/* Action Buttons */}
      <div className="form-actions">
        <button onClick={() => navigate('/ptp')} className="btn btn-secondary">
          Back to Dashboard
        </button>
      </div>

      {!isOnline && (
        <div className="offline-notice">
          ⚠️ You are offline. Signatures will be saved locally and synced when online.
        </div>
      )}
    </div>
  );
}

